import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { TranslocoService } from '@ngneat/transloco';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import {
  FLOATING_PANEL_HEADER_ACTIONS,
  FloatingPanelHeaderAction,
} from '@shared/floating-panel/floating-panel-header-actions.token';
import { FloatingPanelState } from '@shared/floating-panel/floating-panel.types';
import { HdMomentLinkDialogComponent, HdMomentLinkMode } from '../../chart/bodygraph/advanced-properties/hd-moment-link-dialog.component';
import { HdIChingMomentDialogComponent } from '../../iching/moment/hd-iching-moment-dialog.component';
import { HdIChingConsultRequest, HdIChingMomentDialogTarget } from '../../iching/moment/hd-iching-moment.models';
import {
  HdChartGlobalSettingsV1,
  loadHdChartGlobalSettings,
  saveHdChartGlobalSettings,
} from '../../chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdEphemerisLanesComponent } from '../ephemeris-lanes/hd-ephemeris-lanes.component';
import { HdTransitNowNextPanelComponent } from '../now-next-panel/hd-transit-now-next-panel.component';
import { HdTransitMomentAddRequest } from '../transit-moment-add.models';
import { HdTransitsViewStateService } from '../transits-view-state.service';
import { HdTransitsVerticalComponent } from '../vertical/hd-transits-vertical.component';

/** Weergave van het transits-paneel (setting `transitsViewMode`). */
export type HdTransitsViewMode = HdChartGlobalSettingsV1['transitsViewMode'];

/** Zo lang (ms) blijft de "moment bewaard"-bevestiging staan. */
const MOMENT_SAVED_BANNER_MS = 8000;

/**
 * Data van het transits-paneel wanneer het in een floating/dock-panel gemount
 * is (via {@link FLOATING_PANEL_DATA}). Bij direct template-gebruik (de
 * transits-pagina) ontbreekt dit token en gelden de inputs.
 */
export interface HdTransitsPanelData {
  /** Live showChiron-setting van de host-pagina; wint van de `showChiron`-input. */
  readonly showChiron?: boolean;
  /** Compacte paddings in dock-context; wint van de `compact`-input. */
  readonly compact?: boolean;
  /**
   * Opent een chart in de host (de chart-omgeving: nieuwe tab). Zonder deze
   * callback meldt het paneel {@link HdTransitsPanelComponent.chartOpenRequested}.
   */
  readonly openChart?: (chartId: string) => void;
  /**
   * Opent de Just Now-chart (enkel de transit) in de host. Zonder deze callback
   * meldt het paneel {@link HdTransitsPanelComponent.justNowRequested}.
   */
  readonly openJustNow?: () => void;
  /**
   * Opent de Rave New Year-chart (transit-variant) van een jaar in de host.
   * Zonder deze callback meldt het paneel {@link HdTransitsPanelComponent.raveNewYearRequested}.
   */
  readonly openRaveNewYear?: (year: number) => void;
}

/**
 * Transits-container (spec §4 Plaatsing, besluit 2), host-onafhankelijk
 * herbruikt in het floating/dock-panel van de chart-omgeving en op de
 * transits-pagina (`/human-design/transits`). Twee weergaves, te kiezen via
 * de toggle in de toolbar (setting `transitsViewMode`, spec 2026-09-26):
 *
 * - **verticaal** (default): weekstrip + dag-flow + maandkalender
 *   (`hd-transits-vertical`);
 * - **horizontaal**: het Nu & Straks-panel met de ephemeris-lanes eronder.
 *
 * Moment toevoegen (dag-flow of plus-knop op de lanes) opent hier de
 * momentdialoog met chartkeuze (eigen chart voorgeselecteerd, categorie
 * daily); na het bewaren blijft de gebruiker in de transits, met een
 * bevestiging en een "Moment openen"-actie (beslispunt 4).
 *
 * - `showChiron`: default uit de globale chart-settings; in panel-context
 *   stuurt de host-pagina de setting live mee via de panel-data.
 * - Header-action "verkleinen": enkel in floating-modus (de dock-host heeft
 *   geen actions-strook). Togglet de breedte van de zwevende view tussen
 *   ~90vw en ~62vw zodat de rechter dockarea ernaast zichtbaar blijft —
 *   hetzelfde patroon als de wide/narrow-toggle van de doc-viewer.
 */
@Component({
  selector: 'hd-transits-panel',
  imports: [ESegmentedToggleComponent, HdTransitNowNextPanelComponent, HdEphemerisLanesComponent, HdTransitsVerticalComponent, HdMomentLinkDialogComponent, HdIChingMomentDialogComponent],
  templateUrl: './hd-transits-panel.component.html',
  styleUrl: './hd-transits-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.transits-panel-host--floating]': 'isFloatingPanel',
    '[class.transits-panel-host--narrow]': 'narrowActive()',
    '[class.transits-panel-host--sheet]': 'isSheet',
    '[class.transits-panel-host--vertical]': "viewMode() === 'vertical'",
  },
})
export class HdTransitsPanelComponent {
  readonly showChiron = input<boolean>(loadHdChartGlobalSettings().showChiron);
  /** Kleinere paddings in dock-context. */
  readonly compact = input(false);
  /** Een bewaard moment openen, wanneer de panel-data geen `openChart`-callback draagt (transits-pagina). */
  readonly chartOpenRequested = output<string>();
  /** Just Now (transit) bij de nu-lijn, wanneer de panel-data geen `openJustNow`-callback draagt (transits-pagina). */
  readonly justNowRequested = output<void>();
  /** Rave New Year-badge in de dag-flow, wanneer de panel-data geen `openRaveNewYear`-callback draagt. */
  readonly raveNewYearRequested = output<number>();

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdTransitsPanelData | undefined>
    | null;
  private readonly headerActionsSignal = inject(FLOATING_PANEL_HEADER_ACTIONS, { optional: true });
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true }) as
    | { recenter(): void; readonly presentation?: string; readonly state?: Signal<FloatingPanelState> }
    | null;
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly viewState = inject(HdTransitsViewStateService);
  private readonly destroyRef = inject(DestroyRef);

  /** Panel-data (floating/dock) wint; anders de input (template-binding of settings-default). */
  protected readonly effectiveShowChiron = computed(() => this.panelData?.()?.showChiron ?? this.showChiron());
  protected readonly effectiveCompact = computed(() => this.panelData?.()?.compact ?? this.compact());

  /**
   * Zwevende modus: het header-actions token wordt enkel door de
   * floating-panel-host geleverd (de dock-hosts en de pagina doen dat niet).
   */
  protected readonly isFloatingPanel = this.headerActionsSignal != null;
  /** Bottom-sheet op phone: de verticale weergave krijgt daar een eigen hoogte. */
  protected readonly isSheet = this.panelRef?.presentation === 'sheet';
  /**
   * Gedockt in het rechterpaneel (dock-area van de chart-omgeving). Daar is de
   * breedte krap: de dag-flow laat er de "nieuwe poort"-badge weg (de zwaardere
   * stijl van een poortwissel blijft). Zwevend, als sheet en op de pagina blijft
   * de badge staan.
   */
  protected readonly dockedInPanel = computed(() => this.panelRef?.state?.() === 'docked');
  /** Verkleinde zwevende view — de breedte-klasse volgt de globale toggle. */
  protected readonly narrowActive = computed(() => this.isFloatingPanel && !this.isSheet && this.viewState.panelNarrow());

  protected readonly viewMode = signal<HdTransitsViewMode>(loadHdChartGlobalSettings().transitsViewMode);
  protected readonly viewModes: readonly HdTransitsViewMode[] = ['vertical', 'horizontal'];
  private readonly viewModeIcons: Readonly<Record<HdTransitsViewMode, string>> = {
    vertical: 'calendar-week',
    horizontal: 'chart-gantt',
  };

  // ── Moment toevoegen ─────────────────────────────────────────────────────
  protected readonly momentPickerOpen = signal(false);
  protected readonly momentPrefillUtc = signal<string | null>(null);
  protected readonly momentPrefillWithTime = signal(false);
  /** Smaak waarmee de momentdialoog opent: `'iching'` vanuit de scroll-plusjes, anders de datum-smaak. */
  protected readonly momentInitialMode = signal<HdMomentLinkMode | null>(null);
  /** Doel van de I Tjing-momentdialoog (nieuwe raadpleging na de chartkeuze); null = gesloten. */
  protected readonly ichingDialogTarget = signal<HdIChingMomentDialogTarget | null>(null);
  /** Id van het net bewaarde moment (bevestiging met "Moment openen"). */
  protected readonly savedMomentId = signal<string | null>(null);
  private bannerTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly lang = computed(() => {
    // Meelezen zodat de eerste translate ná een async translation-load opnieuw
    // evalueert (zie HdContentLanguageService.translationsLoadedTick).
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly text = computed(() => {
    const lang = this.lang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, lang);
    return {
      title: translate('humanDesign.chart.transits.lanes.title'),
      viewModeLabel: translate('humanDesign.chart.transits.panel.viewModeLabel'),
      viewMode: {
        vertical: translate('humanDesign.chart.transits.panel.viewVertical'),
        horizontal: translate('humanDesign.chart.transits.panel.viewHorizontal'),
      } as Record<HdTransitsViewMode, string>,
      momentSaved: translate('humanDesign.chart.transits.panel.momentSaved'),
      openMoment: translate('humanDesign.chart.transits.panel.openMoment'),
      dismiss: translate('humanDesign.chart.transits.panel.dismiss'),
    };
  });

  /** Icoonknoppen van de weergave-toggle (gedeelde e-segmented-toggle). */
  protected readonly viewModeOptions = computed<readonly ESegmentedToggleOption<HdTransitsViewMode>[]>(() => {
    const text = this.text();
    return this.viewModes.map(mode => ({ value: mode, label: text.viewMode[mode], icon: this.viewModeIcons[mode] }));
  });

  constructor() {
    this.trackFloatingViewLifetime();
    // Floating-panel host-actions strook: verklein-/vergroot-knop links van
    // pin/sluit (spiegelt de wide/narrow-toggle van de doc-viewer). Enkel
    // floating — het token ontbreekt in de dock-host en op de pagina.
    effect(() => {
      // Een telefoon-sheet is altijd volle breedte: geen verklein-knop.
      if (!this.headerActionsSignal || this.isSheet) return;
      const lang = this.contentLang.effectiveLang();
      const narrow = this.viewState.panelNarrow();
      const shrinkAction: FloatingPanelHeaderAction = {
        icon: narrow ? 'up-right-and-down-left-from-center' : 'down-left-and-up-right-to-center',
        tooltip: this.transloco.translate(
          narrow
            ? 'humanDesign.chart.transits.shortcutBar.widenTooltip'
            : 'humanDesign.chart.transits.shortcutBar.shrinkTooltip',
          undefined,
          lang,
        ),
        onClick: () => this.toggleNarrow(),
      };
      this.headerActionsSignal.set([shrinkAction]);
    });
    this.destroyRef.onDestroy(() => this.clearBannerTimer());
  }

  /** Toggle verticaal/horizontaal; de keuze is een gebruikerssetting. */
  protected setViewMode(mode: HdTransitsViewMode): void {
    if (mode === this.viewMode()) return;
    this.viewMode.set(mode);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), transitsViewMode: mode });
  }

  /** Dag-flow of lanes vragen een moment aan: de dialoog met chartkeuze openen. */
  protected openMomentAdd(request: HdTransitMomentAddRequest): void {
    this.momentInitialMode.set(null);
    this.momentPrefillUtc.set(request.atUtc.toISOString());
    this.momentPrefillWithTime.set(request.withTime);
    this.momentPickerOpen.set(true);
  }

  protected onIChingRequested(request: HdIChingConsultRequest): void {
    this.ichingDialogTarget.set({ kind: 'new', ...request });
  }

  /** Moment bewaard: in de transits blijven, met een bevestiging + "Moment openen". */
  protected onMomentCreated(chartId: string): void {
    this.savedMomentId.set(chartId);
    this.clearBannerTimer();
    this.bannerTimer = setTimeout(() => this.savedMomentId.set(null), MOMENT_SAVED_BANNER_MS);
  }

  protected openSavedMoment(): void {
    const chartId = this.savedMomentId();
    if (chartId == null) return;
    this.dismissSavedMoment();
    const openChart = this.panelData?.()?.openChart;
    if (openChart != null) {
      openChart(chartId);
    } else {
      this.chartOpenRequested.emit(chartId);
    }
  }

  /** Just Now-knop op de nu-lijn: de host opent de transit-chart van nu. */
  protected openJustNow(): void {
    const openJustNow = this.panelData?.()?.openJustNow;
    if (openJustNow != null) {
      openJustNow();
    } else {
      this.justNowRequested.emit();
    }
  }

  /** Rave New Year-badge: de host opent de transit-chart van dat jaar. */
  protected openRaveNewYear(year: number): void {
    const openRaveNewYear = this.panelData?.()?.openRaveNewYear;
    if (openRaveNewYear != null) {
      openRaveNewYear(year);
    } else {
      this.raveNewYearRequested.emit(year);
    }
  }

  protected dismissSavedMoment(): void {
    this.clearBannerTimer();
    this.savedMomentId.set(null);
  }

  private clearBannerTimer(): void {
    if (this.bannerTimer != null) clearTimeout(this.bannerTimer);
    this.bannerTimer = null;
  }

  /**
   * Meldt de zwevende modus aan de view-state zolang deze instance leeft, zodat
   * de shift-popup weet of detail-klikken als dialog of gedockt moeten openen.
   */
  private trackFloatingViewLifetime(): void {
    if (!this.isFloatingPanel) return;
    this.viewState.setFloatingViewActive(true, this.isSheet);
    this.destroyRef.onDestroy(() => this.viewState.setFloatingViewActive(false));
  }

  private toggleNarrow(): void {
    const willWiden = this.viewState.panelNarrow();
    this.viewState.togglePanelNarrow();
    // Terug naar de volle breedte → hercentreren zodat het paneel niet deels
    // buiten beeld valt (mirror van de doc-viewer wide-toggle).
    if (willWiden) this.panelRef?.recenter();
  }
}
